import React, { useState, useEffect } from "react";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import useApi from "../utils/useApi";
import { useLoading } from "../context/LoadingContext";
import Toaster, { ToastContainerComponent } from "../components/Toaster";
import MainLayout from "../layouts/MainLayout";

const EmailSettings = () => {
  const { callApi } = useApi();
  const { setLoading } = useLoading();
  const [emailTemplates, setEmailTemplates] = useState({});

  // ** Fetch Email Templates **
  const fetchEmailTemplates = async () => {
    setLoading(true);
    try {
      let updatedTemplates = {};
      for (let type = 1; type <= 4; type++) {
        const response = await callApi({
          endpoint: "/admin/email-template/get_email_template",
          method: "POST",
          data: { email_template_type: type },
        });

        if (response.success) {
          updatedTemplates[type] = response.data.email_template_body || "";
        } else {
          updatedTemplates[type] = ""; // Set empty in case of failure
        }
      }
      setEmailTemplates(updatedTemplates); // ✅ Now update state after fetching all data
    } catch (error) {
      console.error("Error fetching email templates:", error);
      Toaster.error("Error fetching email templates. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailTemplates();
  }, []);

  // ** Helper Function to Get Template Title **
  const getTemplateTitle = (type) => {
    const titles = {
      1: "Create Client Email Template",
      2: "Assigned File Email Template",
      3: "Send Login Credentials To Client",
      4: "Send Login OTP To Users",
    };
    return titles[type] || "Email Template";
  };
  const getTemplateDesc = (type) => {
    const descs = {
      1: "",
      2: "*For generating folder name where a file has been uploaded use identifier ASSIGNED_FOLDER_NAME",
      3: "*For generating email and password use keyword USER_EMAIL_IDENTIFIER & USER_PASSWORD_IDENTIFIER",
      4: "*For generating OTP use keyword USER_EMAIL_OTP",
    };
    return descs[type] || "";
  };

  // ** Update Email Template **
  const updateEmailTemplate = async (type) => {
    setLoading(true);
    try {
      const response = await callApi({
        endpoint: "/admin/email-template/upsert-email-template",
        method: "POST",
        data: {
          email_template_type: type,
          email_template_body: emailTemplates[type],
        },
      });

      if (response.success) {
        Toaster.success("Email template updated successfully!");
      } else {
        Toaster.error(response.message || "Failed to update template.");
      }
    } catch (error) {
      console.error("Error updating email template:", error);
      Toaster.error("Error updating template. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="inner_full_right_part">
        <ToastContainerComponent />
        <div className="row">
          <div className="col-md-12">
            <div className="global_cardbox">
              <div className="global_card_header">
                <div className="global_head_tag">
                  <span>
                    <i className="fa-solid fa-user-plus"></i>
                  </span>
                  Email Settings
                </div>
              </div>

              <div className="create_new_client_holder">
                {/* ✅ Loop Through Templates */}
                {[1, 2, 3, 4].map((type) => (
                  <div className="global_cardbox inner_global_card" key={type}>
                    <div className="global_card_header d-flex">
                      <div className="global_head_tag">
                        <span>{type}</span> {getTemplateTitle(type)}
                      </div>
                    </div>

                    <div className="create_new_client_holder">
                      <div className="basic_info_box">
                        <div className="row">
                          <div className="col-md-12">
                            {/* ✅ SunEditor with Correct Data */}
                            <SunEditor
                              setContents={emailTemplates[type] || ""}
                              onChange={(content) =>
                                setEmailTemplates((prev) => ({
                                  ...prev,
                                  [type]: content,
                                }))
                              }
                              setOptions={{
                                height: 200,
                                buttonList: [
                                  ["undo", "redo"],
                                  ["bold", "italic", "underline", "strike"],
                                  ["removeFormat"],
                                  ["outdent", "indent"],
                                  ["align", "list"],
                                  ["link", "image", "codeView"],
                                ],
                              }}
                            />
                            <br />
                            {getTemplateDesc(type)}
                            
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ✅ Save Button for Each Template */}
                    <div className="update_btn_holder">
                      <button
                        className="btn btn-primary"
                        onClick={() => updateEmailTemplate(type)}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EmailSettings;
